<template>
  <div class="card">
    <div class="card-body pb-3">
      <p class="m-0">
        <small class="font-weight-bold">{{ $t('dashboard.contractor') }}</small>
      </p>
      <div class="select-wrapper">
        <v-select
          v-model="filters.contractors"
          class="sm"
          label="name"
          multiple
          :placeholder="$t('filter-assessment.placeholder.contractor')"
          :options="contractors"
          :clearable="false"
          :reduce="getReduceId"
        />
      </div>
      <div
        v-if="error"
        class="text-danger"
      >
        {{ error }}
      </div>
      <highcharts
        v-else
        :options="chartOptions"
      />
    </div>
  </div>
</template>

<script>
import { format, sub } from 'date-fns'
import Api from '@/utils/Api'
import VSelect from 'vue-select'

export default {
  components: {
    VSelect
  },

  props: {
    filters: {
      type: Object,
      default: () => ({
        period: 'daily',
        startDate: sub(new Date(), { days: 30 }),
        endDate: new Date()
      })
    },
    isApply: {
      type: Boolean,
      default: () => (false)
    }
  },

  data: () => ({
    contractors: [],
    error: '',
    chartOptions: {
      title: false,
      credits: false,
      chart: {
        type: 'solidgauge',
        margin: [0, 0, 0, 0],
        height: 300,
        spacingBottom: 0,
        spacingTop: 0,
        spacingLeft: 0,
        spacingRight: 0,
        events: {
          render () {
            const chart = this
            const label1 = chart.series[0].dataLabelsGroup
            const label2 = chart.yAxis[0].axisGroup

            label1.translate(chart.marginRight, chart.marginBottom - label1.getBBox().height - 10)
            label2.translate(chart.marginRight, chart.marginBottom - label1.getBBox().height + 80)
          }
        }
      },
      pane: {
        startAngle: 0,
        endAngle: 360,
        background: [{
          backgroundColor: '#DEDEDE',
          outerRadius: '110%',
          innerRadius: '70%',
          borderWidth: 0
        }]
      },
      yAxis: {
        min: 0,
        max: 0,
        lineWidth: 0,
        tickPositions: [],
        title: {
          text: '',
          style: {
            color: '#18B2BD',
            fontSize: '15.5px',
            fontWeight: 'bold'
          }
        }
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            enabled: true,
            verticalAlign: 'middle',
            useHTML: true,
            borderWidth: 0
          },
          rounded: true
        }
      },
      series: [{
        name: 'Average Submitter',
        data: [{
          radius: '110%',
          innerRadius: '70%',
          y: 0,
          dataLabels: {
            format:
            '<div style="text-align:center">' +
            '<h6 class="font-weight-bold m-0 p-0">{y}</h6>' +
            '</div>'
          },
          color: {
            linearGradient: {
              x1: 0,
              x2: 1,
              y1: 1,
              y2: 1
            },
            stops: [
              [0, '#4C2E88'],
              [1, '#18B2BD']
            ]
          }
        }]
      }]
    }
  }),

  computed: {
    builtFilters () {
      return [
        `period=${this.filters.period}`,
        `startDate=${format(this.filters.startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(this.filters.endDate, 'yyyy-MM-dd')}`,
        ...this.filters.organization.map(organization => `organization=${organization}`),
        ...this.filters.workLocation.map(workLocation => `workLocation=${workLocation}`),
        ...this.filters.employee.map(employee => `employeeType=${employee}`),
        ...this.filters.contractors.map(contractors => `contractorId=${contractors}`),
        ...this.filters.risk.map(risk => `risk=${risk}`)
      ].join('&')
    }
  },

  watch: {
    'filters.contractors': {
      deep: true,
      immediate: true,
      handler: 'fetch'
    },
    isApply: {
      deep: true,
      immediate: true,
      handler: 'fetch'
    },
    'filters.organization': {
      deep: true,
      immediate: true,
      handler: 'fetchContractors'
    }
  },

  mounted () {
    this.fetch()
    this.fetchContractors()
  },

  methods: {
    async fetch () {
      try {
        this.error = ''
        const { data } = await Api.dashboard.averageContractor(this.builtFilters)
        data.averageSubmitter = Number.isInteger(data.averageSubmitter) ? data.averageSubmitter : parseFloat(data.averageSubmitter.toFixed(2))
        this.chartOptions.yAxis.max = data.totalContractor
        this.chartOptions.yAxis.title.text = data.totalContractor.toString()
        this.chartOptions.series[0].data[0].y = data.averageSubmitter
      } catch {
        this.error = 'Error'
      }
    },
    async fetchContractors () {
      try {
        const { data } = this.filters.organization.length ? await Api.masterData.customContractors({
          Filters: [this.takeAdditionalFilters()].join(',')
        }) : await Api.masterData.customContractors()
        this.contractors = data.data
      } catch {
        this.contractors = []
      }
    },
    getReduceId: ({ id }) => id,
    takeAdditionalFilters () {
      return Object.keys(this.filters)
        .filter(key => !!this.filters[key])
        .filter(key => ['organization'].includes(key))
        .map(key => `${key}==${Array.isArray(this.filters[key]) ? this.filters[key].join('|') : this.filters[key]}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.select-wrapper {
  height: 31px;
  position: relative;
  z-index: 1;
}

::v-deep(.v-select) {
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

::v-deep(.vs__dropdown-toggle) {
  background: white;
}

::v-deep(.vs__selected-options) {
  flex-wrap: nowrap;

  .vs--open & {
    flex-wrap: wrap;
  }
}
</style>
